/**
 * DogDetailModal — Hundedetails mit allen Optionen als Modal.
 *
 * Zeigt dieselbe `DogDetailCard` wie die Hundeverwaltung (`/dog-management-beta`)
 * und bietet dieselben Aktionen: Dokumente/Impfpass ansehen, Nachweise genehmigen
 * oder entziehen, Stammdaten bearbeiten. Wird u. a. aus dem Vorstands-Dashboard
 * geöffnet, damit man von der Hundeübersicht direkt in den Vorgang springen kann.
 *
 * Rahmen ist `ModalShell` (Issue #11) — mobil erscheint es als Slide-up-Sheet
 * über die volle Bildschirmbreite.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { ModalShell } from './core';
import DogDetailCard from '../DogDetailCard';
import DocumentPreviewModal from '../DocumentPreviewModal';
import AddEditDogModal from '../AddEditDogModal';
import ConfirmationModal from '../ConfirmationModal';
import SideDogIcon from '../icons/SideDogIcon';
import GuestBadge from '../GuestBadge';
import { DOG_DOC_TYPE_ID, isGuestOnly, type Dog, type DogDocumentCategory } from '../../types/dog';

interface ConfirmState {
  isOpen: boolean;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant?: 'single' | 'two';
  onConfirm?: () => void;
}

export interface DogDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** Hund, dessen Details gezeigt werden. */
  dogId: number | null;
  /** Vorbelegung, solange der vollständige Datensatz noch lädt. */
  fallbackName?: string;
  /** Halter-Name für die Kopfzeile (falls bekannt). */
  owner?: string;
  /** Halter ist reiner Gast — wird als Badge gezeigt. */
  ownerIsGuest?: boolean;
  /** Wird nach jeder Änderung aufgerufen, damit der Aufrufer neu laden kann. */
  onChanged?: () => void;
  /** Sprung in die Hundeverwaltung. */
  onOpenManagement?: () => void;
}

const DogDetailModal: React.FC<DogDetailModalProps> = ({
  open,
  onClose,
  dogId,
  fallbackName,
  owner,
  ownerIsGuest,
  onChanged,
  onOpenManagement,
}) => {
  const [dog, setDog] = useState<Dog | null>(null);
  const [ownerName, setOwnerName] = useState<string | undefined>(owner);
  const [guestOwner, setGuestOwner] = useState<boolean>(!!ownerIsGuest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>({ isOpen: false, title: '', message: '' });
  const [editOpen, setEditOpen] = useState(false);
  const [preview, setPreview] = useState<{
    isOpen: boolean;
    category: DogDocumentCategory;
  }>({ isOpen: false, category: 'insurance' });

  /**
   * Vollständigen Datensatz laden. `all_dogs_status` ist der einzige Endpoint,
   * der Hund, Halter, Prüfvermerke und Dokumentlisten zusammen liefert.
   */
  const loadDog = useCallback(async () => {
    if (!dogId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/members/all_dogs_status');
      const members = res.data?.data?.members || [];
      let found: Dog | null = null;
      let holder: string | undefined;
      let holderIsGuest: boolean | undefined;
      for (const m of members) {
        const hit = (m.dogs || []).find((d: Dog) => d.dog_id === dogId);
        if (hit) {
          found = hit;
          holder = m.member_name;
          holderIsGuest = m.is_guest ?? isGuestOnly(m.roles);
          break;
        }
      }
      if (!found) {
        setError('Der Hund konnte nicht gefunden werden.');
        setDog(null);
      } else {
        setDog(found);
        setOwnerName(holder || owner);
        if (holderIsGuest !== undefined) setGuestOwner(holderIsGuest);
      }
    } catch (e) {
      console.error('DogDetailModal: Laden fehlgeschlagen', e);
      setError('Die Hundedaten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [dogId, owner]);

  useEffect(() => {
    if (open && dogId) {
      setImageError(false);
      loadDog();
    }
    if (!open) {
      setDog(null);
      setError(null);
    }
  }, [open, dogId, loadDog]);

  /** Genehmigen/Entziehen für Haftpflicht bzw. Tollwut — gleiche Endpunkte wie die Hundeverwaltung. */
  const toggleValidation = (category: DogDocumentCategory) => {
    if (!dog) return;
    const isInsurance = category === 'insurance';
    const currentlyValid = isInsurance ? dog.valid_insurance : dog.valid_tollwut;
    const label = isInsurance ? 'Haftpflichtversicherung' : 'Tollwutimpfung';
    const action = currentlyValid ? 'entziehen' : 'genehmigen';

    setConfirm({
      isOpen: true,
      type: 'question',
      title: `${label} ${action}`,
      message: `Möchten Sie die Gültigkeit der ${label} wirklich ${action}?`,
      variant: 'two',
      onConfirm: async () => {
        const base = isInsurance ? 'insurance' : 'tollwut';
        const endpoint = currentlyValid ? `unvalidate_${base}` : `validate_${base}`;
        try {
          const res = await apiClient.post(`/api/dogs/${endpoint}/${dog.dog_id}`);
          if (!res.data?.success) throw new Error('API Error');
          await loadDog();
          onChanged?.();
          setConfirm({
            isOpen: true,
            type: 'success',
            title: 'Erfolgreich',
            message: `${label} wurde erfolgreich ${currentlyValid ? 'entzogen' : 'genehmigt'}.`,
            variant: 'single',
          });
        } catch {
          setConfirm({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: `Der Status der ${label} konnte nicht geändert werden.`,
            variant: 'single',
          });
        }
      },
    });
  };

  const handleDocumentsChange = async () => {
    await loadDog();
    onChanged?.();
  };

  const title = dog?.hundename || fallbackName || 'Hund';
  const subtitle = ownerName ? (
    <span className="inline-flex items-center gap-1.5">
      Halter: {ownerName}
      {guestOwner && <GuestBadge />}
    </span>
  ) : undefined;

  return (
    <>
      <ModalShell
        open={open}
        onClose={onClose}
        variant="detail"
        size="lg"
        title={title}
        subtitle={subtitle}
        icon={SideDogIcon}
        bodyClassName="p-0"
        footer={
          onOpenManagement ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onOpenManagement}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/50"
              >
                In der Hundeverwaltung öffnen
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ) : undefined
        }
      >
        {loading && !dog ? (
          <div className="space-y-3 p-4">
            <div className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
            <div className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
            <div className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
          </div>
        ) : error ? (
          <p className="p-4 text-sm text-error-600 dark:text-error-400">{error}</p>
        ) : dog ? (
          <DogDetailCard
            dog={dog}
            owner={ownerName}
            ownerIsGuest={guestOwner}
            bare
            imageError={imageError}
            onImageError={() => setImageError(true)}
            onEdit={() => setEditOpen(true)}
            onPreviewDocuments={(category) => setPreview({ isOpen: true, category })}
            onToggleInsurance={() => toggleValidation('insurance')}
            onToggleTollwut={() => toggleValidation('tollwut')}
          />
        ) : null}
      </ModalShell>

      {dog && (
        <DocumentPreviewModal
          isOpen={preview.isOpen}
          onClose={() => setPreview((p) => ({ ...p, isOpen: false }))}
          documents={dog.documents[preview.category] || []}
          category={preview.category}
          categoryId={DOG_DOC_TYPE_ID[preview.category]}
          dogId={dog.dog_id}
          dogName={dog.hundename}
          onDocumentsChange={handleDocumentsChange}
        />
      )}

      {dog && (
        <AddEditDogModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          dog={dog}
          onSuccess={async () => {
            setEditOpen(false);
            await loadDog();
            onChanged?.();
          }}
        />
      )}

      <ConfirmationModal
        visible={confirm.isOpen}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        variant={confirm.variant}
        button={
          confirm.variant === 'single'
            ? { text: 'OK', onPress: () => setConfirm((c) => ({ ...c, isOpen: false })) }
            : undefined
        }
        buttons={
          confirm.variant === 'two'
            ? {
                cancel: {
                  text: 'Abbrechen',
                  onPress: () => setConfirm((c) => ({ ...c, isOpen: false })),
                },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    confirm.onConfirm?.();
                    setConfirm((c) => ({ ...c, isOpen: false }));
                  },
                },
              }
            : undefined
        }
      />
    </>
  );
};

export default DogDetailModal;
